import * as React from "react";
import { connect } from "react-redux";
import { IAppState } from "../models/IAppState"
import ITreatmentPlanTemplateResponseObj from "../models/ITreatmentPlanTemplateResponse";
import TreatmentPlanCard from "../components/TreatmentPlanCard";
import { IPdfTemplates, IConstituentTemplate } from "../models/PdfTemplatesModels";
import { TreatmentPlanActions } from "../actions/TreatmentPlanActions";
import AppUtils from "../utils/AppUtils";
import { Tracker } from "../utils/Tracker";
import {EventNames, SectionContext} from "../utils/EventTrackerConstants";
import LocalStorageUtils from "../utils/LocalStorageUtils";

export interface ViewAllTemplatesModalProps {
    toggleModal: (flag: boolean) => void
    onTreatmentPlanSelect: (template: ITreatmentPlanTemplateResponseObj) => void
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & ViewAllTemplatesModalProps

const ViewAllTemplatesModal: React.FC<Props> = (props) => {

    const [searchText, setSearchText] = React.useState("");
    const [debouncedValue, setDebouncedValue] = React.useState(searchText);
    const [templateList, setTemplateList] = React.useState<Array<ITreatmentPlanTemplateResponseObj>>([]);
    const [pdfTemplates, setPdfTemplates] = React.useState<IPdfTemplates>(null);
    const [noOfStroke, setNoOfStroke] = React.useState(0);

    const section = React.useContext(SectionContext);
    
    React.useEffect(() => {
        fetchTemplatesByName();
    }, [debouncedValue]);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(searchText);
        }, 300);
        return () => {
            clearTimeout(handler);
        };
    }, [searchText]);

    React.useEffect(() => {
        // On Mount
        getConsultationPdfTemplates()
    }, []);

    const getConsultationPdfTemplates = () => {
        props.fetchConsultationPdfTemplates(props.doctorProfile.id, props.doctorProfile.agentCenterMapping[0].centerId, props.doctorProfile.primarySubServiceType.code)
            .then((response: IConstituentTemplate) => {
                const templateIdToNameMap = AppUtils.setPdfTemplatesIdToNameMap(response.constituentTemplates || []);
                const templateNameToDataMap = AppUtils.setPdfTemplateNameToDataMap(response.constituentTemplates || []);
                const responseToSave = { ...response, templateIdToNameMap, templateNameToDataMap };
                setPdfTemplates(responseToSave)
            })
            .catch(() => {
                setPdfTemplates(null)
            });
    }


    const fetchTemplatesByName = () => {
        props.fetchTreatmentPlans(props.doctorProfile.id, debouncedValue)
            .then((response: Array<ITreatmentPlanTemplateResponseObj>) => {
                setTemplateList(response)
            })
            .catch(() => {
                setTemplateList([])
                console.error("Error occoured fetching treatment plans")
            })
    }

    const onBlur = (event: any) => {
        Tracker.track(EventNames.KeyStroke, { value: event.target.value, field: EventNames.Search, noOfKeystrokes: event.target.value?.length, section: section, searchString: event.target.value });
        LocalStorageUtils.setClickEvents({ field: EventNames.Search, flow: EventNames.Treatment_Plan, section: section, value: event.target.value })
    }

    const handleSearch = (event: any) => {
        setNoOfStroke(noOfStroke + 1)
        setSearchText(event.target.value)
    }

    const onExitClick = () => {
        setSearchText("")
        props.toggleModal(false)
    }

    const onListItemClick = (listItem: ITreatmentPlanTemplateResponseObj) => {
        onExitClick()
        props.onTreatmentPlanSelect(listItem)
    }

    return (
        <div className="treatment-plans-page treatment-plans-modal">
            <div className="treatment-modal-header">
                <span>{"Treatment Plans"}</span>
                <img src="/images/icons/cross-grey.svg" onClick={onExitClick} height={17} width={17} />
            </div>
            <span className="horizontal-divider col-xs-12" />
            <div className="header col-xs-12">
                <div className="left-header col-md-4">
                    <div className="view-header">
                        {`All Treatment Plans (${templateList.length})`}
                    </div>
                </div>
                <div className="right-header col-md-5">
                    <div className="search-bar col-md-9 bordered">
                        <img src="/images/search.svg" height={16} width={16} />
                        <input type="text" className="search-text" value={searchText} onChange={handleSearch} onBlur={onBlur} placeholder={""} />
                    </div>
                    {/* TODO: Add after filters */}
                    {/* <div className="filter-options col-md-2 bordered">
                            <img src="/images/icons/down-arrow.svg" height={16} width={16} />
                            <span className="vertical-divider"></span>
                            <img src="/images/icons/down-arrow.svg" height={16} width={16} />
                        </div> */}
                </div>
            </div>
            <div className="body col-xs-12">
                {templateList.map((template: ITreatmentPlanTemplateResponseObj) => {
                    return (
                        <div key={template.id} className="treatment-card">
                            <TreatmentPlanCard
                                listItem={template}
                                editMode={false}
                                pdfTemplates={pdfTemplates}
                                onListItemClick={onListItemClick} />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function mapStateToProps(state: IAppState) {
    return {
        doctorProfile: state.doctorProfile
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        fetchTreatmentPlans: (doctorId: number, query: string): Promise<Array<ITreatmentPlanTemplateResponseObj>> => {
            if (query) {
                return TreatmentPlanActions.fetchTreatmentPlansByName(doctorId, query)
            }
            else {
                return TreatmentPlanActions.fetchAllTreatmentPlanTemplates(doctorId, dispatch)
            }
        },
        fetchConsultationPdfTemplates: (doctorId: number, centerId: number, doctorType: string): Promise<IConstituentTemplate> => {
            return TreatmentPlanActions.fetchConsultationPdfTemplates(doctorId, centerId, doctorType)
        }
    }
};

export default connect<ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps>, ViewAllTemplatesModalProps>(
    mapStateToProps,
    mapDispatchToProps
)(ViewAllTemplatesModal);